import { logout, useAuth, useTenant } from '@/auth';
import { isRecruit41Domain } from '@/auth/useTenant';
import LoginModal from '@/auth/LoginModal';
import { useGoogleOneTap } from '@/auth/useGoogleOneTap';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard } from 'lucide-react';
import { TenantLogo } from '@/common/components/TenantLogo';
import { captureEvent } from '@/posthog-analytics/posthog';
import { useQuery } from '@tanstack/react-query';
import { getMyPermissionsOptions } from '@/api/query';

interface HeaderProps {
  hideSignIn?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ hideSignIn = false }) => {
  const tenant = useTenant();
  const { user, isAuthenticated, refreshAuth } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Google One Tap / FedCM auto sign-in. On the job portal we only
  // attempt it AFTER the user opens the login modal (clicks "Sign in") — not on
  // page load — so the prompt is tied to an explicit sign-in intent. The hook
  // no-ops once authenticated; the membership gate on the backend keeps it safe
  // for candidates (non-members 403 and fall back to the modal's button).
  useGoogleOneTap({
    enabled: showLoginModal && !isAuthenticated,
    onSuccess: refreshAuth,
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const { data: permissions } = useQuery({
    ...getMyPermissionsOptions(),
    enabled: isAuthenticated,
  });

  const shouldShowEmployerRedirection = !/^\/job\/[^/]+$/.test(
    location.pathname,
  );

  // Check if user has any permissions to access ATS system (tenant-level OR job-level)
  const hasAtsPermissions = isAuthenticated &&
    permissions != null &&
    (permissions.tenant_permissions.length > 0 || permissions.job_permissions.length > 0);

  const handleLogout = () => {
    logout();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close login modal when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [isAuthenticated, showLoginModal]);

  return (
    <>
    <header className="bg-white/80 backdrop-blur-lg sticky top-0 z-50 border-b border-gray-100/60">
      <div className="mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center group">
            <TenantLogo domain={tenant.domain} name={tenant.name} className={isRecruit41Domain() ? 'h-6 sm:h-7 md:h-8 max-w-[180px] object-contain' : undefined} />
          </Link>

          <nav className="flex items-center gap-3">
            {shouldShowEmployerRedirection && hasAtsPermissions && (
              <Link
                to="/ats"
                onClick={() => captureEvent('employer_dashboard_clicked', { source: 'job_portal_header' })}
                className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                <LayoutDashboard className="h-4 w-4" />
                Go to Employer Dashboard
              </Link>
            )}

            {!isAuthenticated && !hideSignIn && (
              <button
                onClick={() => setShowLoginModal(true)}
                className="text-sm font-medium px-5 py-2 rounded-xl transition-all"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }}
              >
                Sign in
              </button>
            )}

            {isAuthenticated && user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 text-sm text-gray-600 px-3 py-2 rounded-xl hover:bg-gray-50 transition-all"
                >
                  <div
                    className="h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold"
                    style={{
                      backgroundColor: 'hsl(var(--primary) / 0.1)',
                      color: 'hsl(var(--primary))',
                    }}
                  >
                    {user.display.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium underline">{user.email}</span>
                  <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl border border-gray-100 shadow-lg py-1 z-50">
                    <button
                      onClick={() => {
                        setDropdownOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>
      </div>

    </header>

      {/* Login Modal - outside header to avoid sticky/z-index clipping */}
      {showLoginModal && (
        <LoginModal
          isDashboardLogin={true}
          hideCloseButton={false}
          isProtectedRoute={false}
          onClose={() => setShowLoginModal(false)}
        />
      )}
    </>
  );
};
